import { useEffect, useState } from 'react';
import { loadJson } from './loader';
import { useDataSource } from '../data-source/provider';
import { useTrack } from './TrackContext';
import type { DataSource as Source } from '../data-source/types';
import type { TrackId } from './types';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

const m = defineMessages({
  noFile: {
    id: 'data.track.noFile',
    defaultMessage: 'файл не выбран',
    description: 'Причина, почему блок пуст (строчная фраза): экран не назвал, какие данные читать',
  },
  noUrl: {
    id: 'data.track.noUrl',
    defaultMessage: 'адрес не задан',
    description: 'Причина, почему блок пуст (строчная фраза): не указан адрес файла данных',
  },
});

export type DataState = 'loading' | 'ready' | 'missing';

/** Откуда фактически пришли данные: выбранный трек или dev-заглушка. */
export type DataSource = 'track' | 'stub';

export interface TrackData<T> {
  state: DataState;
  data: T | null;
  /** Причина отсутствия — показывается словами, а не молчанием. */
  reason: string | null;
  source: DataSource | null;
  track: TrackId;
}

const LOADING = <T,>(track: TrackId): TrackData<T> => ({
  state: 'loading',
  data: null,
  reason: null,
  source: null,
  track,
});

/**
 * Читает именованный набор среза через ИСТОЧНИК ДАННЫХ ТЕКУЩЕГО МАРШРУТА.
 *
 * Здесь читается именно `current` — та реализация, которую объявил маршрут
 * (`data-source/routes.ts`). Это и есть смысл всего слоя: перевод экрана на
 * канон должен быть правкой ОДНОЙ строки в таблице источников. Читай хук
 * `fixtures` напрямую — строка в таблице правилась бы, а экран продолжал бы
 * показывать фикстурные числа, уже без пометки о происхождении и с
 * каноническими органами управления срезом рядом. Инвариант 5 запрещает
 * смешивать эпистемические статусы, поэтому связь «объявление маршрута →
 * фактический источник» держится здесь, а не обещанием в комментарии.
 *
 * Ничего не бросает: отсутствие данных возвращается состоянием.
 */
export function useTrackData<T>(file: string | null): TrackData<T> {
  return useSourceData<T>(file, useDataSource().current);
}

/**
 * То же чтение, но ЯВНО из фикстур, независимо от объявления маршрута.
 *
 * Нужен ровно одному потребителю — общей оболочке (`shell/Topbar.tsx`):
 * поиск по контурам и хозяйствам и дата набора относятся к наборам прототипа
 * и стоят на всех экранах сразу, поэтому «источник текущего маршрута» для них
 * ничего не значит. Имя хука говорит это вслух, чтобы фикстура в оболочке не
 * выглядела каноном.
 */
export function useFixtureData<T>(file: string | null): TrackData<T> {
  return useSourceData<T>(file, useDataSource().fixtures);
}

function useSourceData<T>(file: string | null, source: Source): TrackData<T> {
  const { track } = useTrack();
  const [result, setResult] = useState<TrackData<T>>(() => LOADING<T>(track));

  useEffect(() => {
    if (!file) {
      setResult({
        state: 'missing',
        data: null,
        reason: tr(m.noFile),
        source: null,
        track,
      });
      return;
    }

    let alive = true;
    setResult(LOADING<T>(track));

    void (async () => {
      const read = await source.read<T>(file);
      if (!alive) return;
      if (read.ok) {
        setResult({ state: 'ready', data: read.value, reason: null, source: 'track', track });
        return;
      }
      setResult({ state: 'missing', data: null, reason: read.reason, source: null, track });
    })();

    return () => {
      alive = false;
    };
  }, [file, track, source]);

  // Never label the previous dataset as the newly selected track for one render.
  return result.track === track ? result : LOADING<T>(track);
}

/** Загрузка файла вне трека (например, общая геометрия) с тем же контрактом. */
export function useJsonFile<T>(url: string | null, fallbackUrl?: string): TrackData<T> {
  const { track } = useTrack();
  const [result, setResult] = useState<TrackData<T>>(() => LOADING<T>(track));

  useEffect(() => {
    if (!url) {
      setResult({ state: 'missing', data: null, reason: tr(m.noUrl), source: null, track });
      return;
    }

    let alive = true;
    setResult(LOADING<T>(track));

    void (async () => {
      const first = await loadJson<T>(url);
      if (!alive) return;
      if (first.ok) {
        setResult({ state: 'ready', data: first.value, reason: null, source: 'track', track });
        return;
      }

      if (fallbackUrl) {
        const second = await loadJson<T>(fallbackUrl);
        if (!alive) return;
        if (second.ok) {
          setResult({ state: 'ready', data: second.value, reason: null, source: 'stub', track });
          return;
        }
      }

      setResult({ state: 'missing', data: null, reason: first.reason, source: null, track });
    })();

    return () => {
      alive = false;
    };
  }, [url, fallbackUrl, track]);

  return result;
}
